import React, { useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  intro: {
    color: '#c9cff8',
    fontSize: 13,
    lineHeight: 1.55,
    marginBottom: 12,
  },
  buttonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  actionButton: {
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    justifyContent: 'flex-start',
    padding: '8px 12px',
    color: '#e5e7ff',
    borderColor: 'rgba(77, 121, 255, 0.26)',
    background: 'rgba(77, 121, 255, 0.1)',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.42)',
      background: 'rgba(77, 121, 255, 0.16)',
    },
  },
  disabledButton: {
    color: '#8187b8 !important',
    borderColor: 'rgba(255,255,255,0.08) !important',
    background: 'rgba(255,255,255,0.04) !important',
  },
  buttonLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  buttonTitle: {
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.3,
  },
  buttonMeta: {
    fontSize: 11,
    color: '#aab0df',
    lineHeight: 1.35,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
  status: {
    color: '#9ef7c5',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function normalizeChain(chain) {
  const raw = String(chain || '').trim().toLowerCase();
  if (!raw) return 'ethereum';
  if (raw === 'eth' || raw === 'ethereum' || raw === 'mainnet') return 'ethereum';
  if (raw === 'arb' || raw === 'arbitrum') return 'arbitrum';
  if (raw === 'base') return 'base';
  if (raw === 'optimism' || raw === 'op') return 'optimism';
  if (raw === 'polygon' || raw === 'matic') return 'polygon';
  if (raw === 'bsc' || raw === 'binance' || raw === 'binance smart chain') return 'bsc';
  return raw;
}

function isSolChain(chain) {
  return chain === 'sol' || chain === 'solana';
}

function getExplorerBase(chain) {
  if (isSolChain(chain)) return 'https://solscan.io';
  if (chain === 'arbitrum') return 'https://arbiscan.io';
  if (chain === 'base') return 'https://basescan.org';
  if (chain === 'optimism') return 'https://optimistic.etherscan.io';
  if (chain === 'polygon') return 'https://polygonscan.com';
  if (chain === 'bsc') return 'https://bscscan.com';
  return 'https://etherscan.io';
}

function buildExplorerUrl(address, chain) {
  if (!address) return '';
  if (isSolChain(chain)) return `https://solscan.io/token/${address}`;
  return `${getExplorerBase(chain)}/token/${address}`;
}

function buildDexscreenerUrl(address, chain) {
  if (!address) return '';
  const chainKey = isSolChain(chain) ? 'solana' : (chain === 'ethereum' ? 'ethereum' : chain);
  return `https://dexscreener.com/${chainKey}/${address}`;
}

function buildCoinGeckoUrl(coingeckoId) {
  if (!coingeckoId) return '';
  return `https://www.coingecko.com/en/coins/${encodeURIComponent(coingeckoId)}`;
}

function buildUniswapUrl(address, chain) {
  if (!address) return '';
  if (isSolChain(chain)) return `https://raydium.io/swap/?outputCurrency=${encodeURIComponent(address)}`;
  const chainParam = chain === 'ethereum' ? 'mainnet' : chain;
  return `https://app.uniswap.org/#/swap?outputCurrency=${encodeURIComponent(address)}&chain=${encodeURIComponent(chainParam)}`;
}

function openUrl(url) {
  if (!url || typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function copyText(value) {
  if (!value) throw new Error('Nothing to copy');
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document === 'undefined') throw new Error('Clipboard unavailable');
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', 'readonly');
  input.style.position = 'absolute';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
}

function ActionButton({ classes, title, meta, disabled, onClick }) {
  return (
    <Button
      variant="outlined"
      className={`${classes.actionButton}${disabled ? ` ${classes.disabledButton}` : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <Box className={classes.buttonLabel}>
        <Typography className={classes.buttonTitle}>{title}</Typography>
        <Typography className={classes.buttonMeta}>{meta}</Typography>
      </Box>
    </Button>
  );
}

export default function ExternalLinksPanel({ address, metadata }) {
  const classes = useStyles();
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const chain = normalizeChain(metadata && metadata.chain);
  const pageUrl = useMemo(() => {
    if (!address || typeof window === 'undefined') return '';
    return `${window.location.origin}/token/${address}`;
  }, [address]);

  const links = useMemo(() => ({
    explorer: buildExplorerUrl(address, chain),
    dexscreener: buildDexscreenerUrl(address, chain),
    coingecko: buildCoinGeckoUrl(metadata && metadata.coingecko_id),
    uniswap: buildUniswapUrl(address, chain),
  }), [address, chain, metadata]);

  const handleCopyAddress = async () => {
    setStatus('');
    setError('');
    try {
      await copyText(address || '');
      setStatus('Token address copied.');
    } catch (e) {
      setError('Failed to copy token address.');
    }
  };

  const handleCopyPageUrl = async () => {
    setStatus('');
    setError('');
    try {
      await copyText(pageUrl || '');
      setStatus('Token page URL copied.');
    } catch (e) {
      setError('Failed to copy page URL.');
    }
  };

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>External Links</Typography>
      <Divider className={classes.divider} />
      <Typography className={classes.intro}>
        Jump into market tools, explorer views, or copy token references for deeper investigation.
      </Typography>

      <Box className={classes.buttonGrid}>
        <ActionButton classes={classes} title="Explorer" meta={links.explorer ? `Open ${chain} token page` : 'Explorer unavailable'} disabled={!links.explorer} onClick={() => openUrl(links.explorer)} />
        <ActionButton classes={classes} title="Dexscreener" meta={links.dexscreener ? 'Open market chart view' : 'Dexscreener unavailable'} disabled={!links.dexscreener} onClick={() => openUrl(links.dexscreener)} />
        <ActionButton classes={classes} title="CoinGecko" meta={links.coingecko ? 'Open token market profile' : 'No CoinGecko mapping'} disabled={!links.coingecko} onClick={() => openUrl(links.coingecko)} />
        <ActionButton classes={classes} title="Uniswap" meta={links.uniswap ? 'Open swap route' : 'Swap route unavailable'} disabled={!links.uniswap} onClick={() => openUrl(links.uniswap)} />
        <ActionButton classes={classes} title="Copy Address" meta={address ? 'Copy token contract address' : 'No token address'} disabled={!address} onClick={handleCopyAddress} />
        <ActionButton classes={classes} title="Copy Page URL" meta={pageUrl ? 'Copy shareable token page link' : 'Page URL unavailable'} disabled={!pageUrl} onClick={handleCopyPageUrl} />
      </Box>

      <Typography className={classes.footer}>
        {address ? `Chain ${chain} • Address ${String(address).slice(0, 6)}...${String(address).slice(-4)}` : 'Token address unavailable.'}
      </Typography>
      {status ? <Typography className={classes.status}>{status}</Typography> : null}
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
